//External libs
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import MetricsModel from './metrics.model';

/**
 * @service MetricsService
 * @description Service for managing and retrieving aquaculture metrics data.
 * This service is primarily responsible for fetching aggregated metrics used for visualization,
 * such as graphs and charts within the application.
 *
 * It interacts with the `/blubay/metrics/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Data Retrieval**: Fetching metrics data associated with specific farm sites.
 * 2. **Graph Data Support**: Providing data structures suitable for plotting trends (e.g., water quality parameters over time).
 * 3. **Resource Management**: Handling the lifecycle of HTTP requests to prevent memory leaks.
 *
 * **Data Flow:**
 * - Component calls `getMetricsDataGraph` with a `farmSiteId`.
 * - Service sends a GET request to the backend with specific flags.
 * - Backend returns an array of `MetricsModel`.
 * - Service resolves the promise with the data.
 *
 * @feature 1. Retrieve metrics data for graphing.
 * @feature 2. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<MetricsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/metrics/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class MetricsService extends Service<MetricsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/metrics/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/metrics/');
  }

  /**
   * @method getMetricsDataGraph
   * @async
   * @description Fetch metrics data for a specific farm site to generate graphs.
   *
   * This method performs a GET request to the service endpoint with query parameters:
   * - `farm_site_id`: The ID of the site to fetch data for.
   * - `flag=true`: Likely indicates a request for processed/graph-ready data.
   * - `order=new`: Sorts results by newest first.
   *
   * @param {string} farmSiteId - The unique identifier of the farm site.
   * @returns {Promise<Array<MetricsModel>>} Promise resolving to an array of MetricsModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * metricsService.getMetricsDataGraph('site-123').then(metrics => {
   *   this.renderChart(metrics);
   * });
   */
  public getMetricsDataGraph(farmSiteId: string) {
    return new Promise<Array<MetricsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<MetricsModel>>(`${this.baseUrl}${this.serviceUrl}?farm_site_id=${farmSiteId}&flag=true&order=new`, this.httpOptions)
        .subscribe(response => {
          resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
        this.ongoingRequests.push(request);
      });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
